import type { CampaignSchema, Status } from "@/types";

/**
 * Campaign lifecycle status helpers (generated `Status = "SC" | "AC" | "FI" | "CA"`).
 *
 * The widget only serves ACTIVE and FINISHED campaigns: the backend drops
 * cancelled (`"CA"`) and scheduled (`"SC"`) campaigns before they reach the client,
 * so the FE buckets only those two — anything else falls into neither list (hidden).
 * Single source of truth so the active/finished split can't drift across call sites.
 */
export const isActiveCampaignStatus = (status: Status): boolean =>
  status === "AC";

export const isFinishedCampaignStatus = (status: Status): boolean =>
  status === "FI";

/**
 * Whether a finished campaign is still inside its post-`end` display window —
 * `display_until` is `end + overlay.display_time`, computed by the backend.
 *
 * The `campaigns` stream serves the UNION of "still inside its window" and "among
 * the last N campaigns to end", because the two surfaces want different sets: the
 * in-game dropdown, launcher button and auto-select show only what is still
 * in-window, while the open widget's results grid shows every finished campaign
 * served. The stream is rendered once and fanned out to both build targets, so the
 * narrowing has to happen here rather than server-side.
 *
 * A null `display_until` means no window is configured, so never in one.
 *
 * Compared against `Date.now()` when the selector runs, so a campaign leaves the
 * in-game surfaces on the first store tick after its window closes — the
 * `campaigns` stream ticks every 60s, the same latency the server-side filter had.
 */
export const isWithinDisplayWindow = (campaign: CampaignSchema): boolean =>
  campaign.display_until != null &&
  new Date(campaign.display_until).getTime() > Date.now();
